import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import useSchedule from "../../hooks/useShedule";

import CustomButton from "../../Components/CustomButton";
import ModalWarning from "../../Components/ModalWarning";
import OnetimeFlow from "../../Components/CreateSchedule/OnetimeFlow";
import IndividualFlow from "../../Components/CreateSchedule/IndividualFlow";
import GroupFlow from "../../Components/CreateSchedule/GroupFlow";
import ProgressCircle from "../../Components/ProgressCircle";
import BackButton from "../../Components/BackButton";
import Loader from "../../Components/Loader";
import {
  scheduleTypes,
  getProgresBarTitles,
} from "../../Components/CreateSchedule/constants";

import { isValidEmail } from "../../utils/helpers";

import { ReactComponent as CalendarIcon } from "../../static/images/calendar_green.svg";
import { ReactComponent as IndividualIcon } from "../../static/images/individual_icon_green.svg";
import { ReactComponent as GroupIcon } from "../../static/images/group_icon_green.svg";
import { ReactComponent as Tick } from "../../static/images/tick_sign.svg";

import "./style.scss";

const CreateSchedule = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    currentStep,
    totalSteps,
    selectedScheduleType,
    formData,
    formDataChanged,
    currency,
    invitedCustomers,
    selectedDayAndTime,
    errors,
    loading,
    maxMembersHasError,
    warningModalToggle,
    setCurrentStep,
    setWarningModalToggle,
    handleBack,
    handleNext,
    handleSelectDay,
    handleSelectScheduleType,
    handleInviteCustomer,
    handleDeleteCustomer,
    setInviteInputValue,
    handleFormFieldChange,
    postInvitation,
    createSchedule,
  } = useSchedule();

  const progresBarTitles = getProgresBarTitles(t);

  const scheduleTypeCards = [
    {
      type: scheduleTypes.oneTime,
      icon: <CalendarIcon />,
      description: t("createSchedule.schedule_type_one_time"),
    },
    {
      type: scheduleTypes.individual,
      icon: <IndividualIcon />,
      description: t("createSchedule.schedule_type_individual"),
    },
    {
      type: scheduleTypes.group,
      icon: <GroupIcon />,
      description: t("createSchedule.schedule_type_group"),
    },
  ];

  const progresBarTitleKey =
    selectedScheduleType === scheduleTypes.oneTime ? "oneTime" : "group";

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (currentStep !== totalSteps) return;

    const invitations =
      invitedCustomers.length > 0
        ? invitedCustomers.map((customer) =>
            isValidEmail(customer.contact)
              ? {
                  customerEmailAddress: customer.contact,
                }
              : {
                  customerPhoneNumber: customer.contact,
                },
          )
        : [];

    if (selectedScheduleType === scheduleTypes.oneTime) {
      postInvitation(invitations);
    } else {
      createSchedule(invitations);
    }
  };

  return (
    <div className="create-schedule-container">
      {loading && <Loader />}
      <ModalWarning
        warningText={t("createSchedule.schedule_created_success")}
        buttonText={t("go_main_page")}
        changeModal={setWarningModalToggle}
        modalValue={warningModalToggle}
        Image={Tick}
        handleButton={() => {
          localStorage.setItem("requestNotificationPermissionProvider", true);
          window.dispatchEvent(new Event("localStorageChangedNotifications"));
          navigate("/sessions");
        }}
      />
      <div className="progress-fixed-container">
        <div className="progress-bar">
          <ProgressCircle totalSteps={totalSteps} currentStep={currentStep} />
          <div className="labels">
            <h2>
              {progresBarTitles[`step${currentStep}`][progresBarTitleKey].title}
            </h2>
            <p>
              {
                progresBarTitles[`step${currentStep}`][progresBarTitleKey]
                  .subTitle
              }
            </p>
          </div>
        </div>
      </div>
      <form onSubmit={handleFormSubmit}>
        <>
          {currentStep === 1 ? (
            <div className="selecion-wrapper">
              {scheduleTypeCards.map((item, index) => (
                <div
                  key={`${item.type}-${index}`}
                  className="type-card"
                  onClick={() => handleSelectScheduleType(item.type)}
                >
                  <div className="title-wrapper">
                    {item.icon}
                    <span className="title">{item.type}</span>
                  </div>
                  <p className="description">{item.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div>
              {selectedScheduleType === scheduleTypes.oneTime && (
                <OnetimeFlow
                  currentStep={currentStep}
                  formData={formData}
                  invitedCustomers={invitedCustomers}
                  errors={errors}
                  currency={currency}
                  setInviteInputValue={setInviteInputValue}
                  inviteCutomer={handleInviteCustomer}
                  deleteCustomer={handleDeleteCustomer}
                  handleFormFieldChange={handleFormFieldChange}
                />
              )}
              {selectedScheduleType === scheduleTypes.individual && (
                <IndividualFlow
                  currentStep={currentStep}
                  formData={formData}
                  invitedCustomers={invitedCustomers}
                  selectedDayAndTime={selectedDayAndTime}
                  errors={errors}
                  currency={currency}
                  setInviteInputValue={setInviteInputValue}
                  inviteCutomer={handleInviteCustomer}
                  deleteCustomer={handleDeleteCustomer}
                  handleFormFieldChange={handleFormFieldChange}
                  setSelectedDayAndTime={handleSelectDay}
                />
              )}
              {selectedScheduleType === scheduleTypes.group && (
                <GroupFlow
                  currentStep={currentStep}
                  formData={formData}
                  invitedCustomers={invitedCustomers}
                  selectedDayAndTime={selectedDayAndTime}
                  errors={errors}
                  currency={currency}
                  maxMembersHasError={maxMembersHasError}
                  setInviteInputValue={setInviteInputValue}
                  inviteCutomer={handleInviteCustomer}
                  deleteCustomer={handleDeleteCustomer}
                  handleFormFieldChange={handleFormFieldChange}
                  setSelectedDayAndTime={handleSelectDay}
                />
              )}
            </div>
          )}
          <div className="fixed-container">
            <div className="create-schedule-buttons-wrapper">
              <BackButton
                onClick={handleBack}
                onModalConfirm={() => setCurrentStep(1)}
                showDataLoseWarning={formDataChanged && currentStep === 2}
              />
              {currentStep > 1 && currentStep < totalSteps && (
                <CustomButton onClick={handleNext}>
                  {currentStep !== totalSteps - 1
                    ? t("next")
                    : t(
                        invitedCustomers.length === 0
                          ? "skip_and_review"
                          : "review",
                      )}
                </CustomButton>
              )}
              {currentStep === totalSteps && (
                <CustomButton type="submit">{t("confirm")}</CustomButton>
              )}
            </div>
          </div>
        </>
      </form>
    </div>
  );
};

export default CreateSchedule;
